/**
 * Ad Manager Module
 * Handles ad slot creation and mapping
 */

import { GPTLoader } from './gpt-loader';
import { AdSlot } from './ad-slot';

export class AdManager {
  private gptLoader: GPTLoader;
  private slots: googletag.Slot[] = [];
  public enableDebugLog: boolean = false;

  constructor() {
    this.gptLoader = GPTLoader.getInstance();
  }

  /**
   * Logs messages to console if debug logging is enabled
   * @param args Arguments to pass to console.log
   */
  public log(...args: any[]): void {
    if (this.enableDebugLog) {
      console.log('[Ad Handler]', ...args);
    }
  }

  public setTargeting(key: string, values: string | string[]): void {
    if(window.googletag) {
      window.googletag.cmd.push(() => {
        const pubads = window.googletag.pubads();
        pubads.setTargeting(key, values);
      });
    } else {
      this.log('googletag is not defined yet, setTargeting lost data');
    }
  }

  /**
   * Makes the label div visible for a given ad div ID
   * @param adDivId The ID of the ad div
   */
  public showAdLabel(adDivId: string): void {
    const labelId = `${adDivId}-label`;
    const labelElement = document.getElementById(labelId);
    
    if (labelElement) {
      labelElement.style.display = 'block';
      this.log(`Made label visible for: ${labelId}`);
    } else {
      this.log(`Label element not found: ${labelId}`);
    }
  }

  /**
   * Sets up listener for ad load events
   */
  public setupAdLoadListener(): void {
    const googletag = window.googletag;
    
    // Listen for the slotRenderEnded event which fires when an ad is loaded
    googletag.pubads().addEventListener('slotRenderEnded', (event: googletag.events.SlotRenderEndedEvent) => {
      const slot = event.slot;
      const divId = slot.getSlotElementId();
      
      this.log(`Ad render event for slot: ${divId}`);
      
      // Make the corresponding label div visible only if ad is not empty
      if (!event.isEmpty) {
        this.log(`Ad successfully rendered in slot: ${divId}`);
        this.showAdLabel(divId);
      } else {
        this.log(`No ad returned for slot: ${divId}`);
      }
    });
    
    // Also listen for slot response to debug
    googletag.pubads().addEventListener('slotResponseReceived', (event: googletag.events.SlotResponseReceived) => {
      this.log('Slot response received:', event.slot.getSlotElementId());
    });
    
    // Listen for slot requested
    googletag.pubads().addEventListener('slotRequested', (event: googletag.events.SlotRequestedEvent) => {
      this.log('Slot requested:', event.slot.getSlotElementId());
    });
    
    this.log('Ad load listeners setup complete');
  }

  /**
   * Adds a slot to the internal slots array
   * @param slot The slot to add
   */
  public addSlot(slot: googletag.Slot): void {
    this.slots.push(slot);
  }

  /**
   * Gets the GPT loader instance
   */
  public getGPTLoader(): GPTLoader {
    return this.gptLoader;
  }

  /**
   * Refreshes all ad slots
   */
  public refreshSlots(): void {
    if (this.slots.length > 0) {
      this.gptLoader.ready(() => {
        window.googletag.pubads().refresh(this.slots);
        this.log('Ad slots refreshed');
      });
    }
  }

  /**
   * Clears all ad slots
   */
  public clearSlots(): void {
    if (this.slots.length > 0) {
      this.gptLoader.ready(() => {
        window.googletag.destroySlots(this.slots);
        this.slots = [];
        this.log('All ad slots cleared');
      });
    }
  }

  /**
   * Gets the current ad slots
   */
  public getSlots(): googletag.Slot[] {
    return this.slots;
  }

  // performs everything needed for mapping ad slots for an entire page
  public mapping = ( adSlots: AdSlot[] ) => {
    this.getGPTLoader().ready(() => {
      const googletag = window.googletag;
      
      // Set up ad load listener
      this.setupAdLoadListener();
      adSlots.forEach(adSlot => {
        if(adSlot.isInterstitial) {
          const interstitialSlot = googletag.defineOutOfPageSlot(adSlot.adUnitPath, googletag.enums.OutOfPageFormat.INTERSTITIAL);
          if (interstitialSlot) {
            interstitialSlot.setConfig({
              interstitial: {
                triggers: {
                  navBar: true,
                  unhideWindow: true,
                },
              },
            });
            interstitialSlot.addService(googletag.pubads());
            this.addSlot(interstitialSlot);
          }
        } else {
          const gptSlot = googletag.defineSlot(
            adSlot.adUnitPath,
            adSlot.sizes,
            adSlot.div
          );
          if (gptSlot) {
            if(adSlot.adSizeMappings) {
              const googleMapping = googletag.sizeMapping();
              adSlot.adSizeMappings.forEach(m => {
                googleMapping.addSize(m.viewportSize, m.adSizes);
              });
              gptSlot.defineSizeMapping(googleMapping.build());
            }
            gptSlot.addService(googletag.pubads());
            this.addSlot(gptSlot);
          }
        }
      });
      
      googletag.pubads().enableSingleRequest();
      googletag.pubads().enableLazyLoad({
        fetchMarginPercent: 2,  // Fetch when within x% of viewport
        renderMarginPercent: 2  // Render when within x% of viewport
      });
      // Enable services (triggers display of all slots)
      googletag.enableServices();
      this.slots.forEach(slot => {
        googletag.display(slot);
      });
      this.log('ad mapping completed. Total slots created:', this.getSlots().length);
    });
  };

  public addStickyness(stickyAdDivs:HTMLCollectionOf<Element>): void {
      window.addEventListener('scroll', () => {
          const minY = 0;
          Array.from(stickyAdDivs).forEach(stickyDiv => {
              const stickyDivParent = stickyDiv.parentElement as HTMLElement;
              const rect = stickyDivParent.getBoundingClientRect();        
              if (rect.top < minY) {
                  (stickyDiv as HTMLElement).style.position = 'fixed';
                  (stickyDiv as HTMLElement).style.top = `${minY}px`;
              } else {
                  (stickyDiv as HTMLElement).style.position = 'relative';
                  (stickyDiv as HTMLElement).style.top = '';
              }
          });
      });
  }

}
